import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stack,
  Paper
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LearningProgressModal from './LearningProgressModal';
import LearnerReadingBody from './LearnerReadingBody';
import { filterChildrenForContentGeneration } from '../utils/contentGenerationSubtasks';
import { outcomeModuleDisplayName } from '../utils/outcomeModuleName';
import { wordRangeFromCredits } from '../utils/learningContentSizing';

export default function LearningBuilder({ modules, assignedItems, qualification }) {
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [runId, setRunId] = useState(null);
  const [debugLogs, setDebugLogs] = useState([]);
  const [status, setStatus] = useState(null);
  
  // Build context tree from modules and assigned items
  const contextTree = useMemo(() => {
    if (!modules || !assignedItems) return null;
    
    return {
      program: {
        name: qualification?.name || qualification?.title || 'Learning programme',
        ...(qualification?.nqf_level != null && qualification?.nqf_level !== ''
          ? { level: Number(qualification.nqf_level) }
          : {}),
        credits: qualification?.total_credits || 120
      },
      modules: modules.map(module => {
        const assignments = assignedItems[module.id] || { KT: [], PS: [] };
        const ktItems = assignments.KT || [];
        const psItems = assignments.PS || [];
        
        return {
          code: module.elo_number || `ELO${module.id}`,
          name: outcomeModuleDisplayName(module),
          description: module.description || '',
          credits: module.credits || 0,
          tasks: [
            ...ktItems.map(item => ({
              code: item.code,
              name: item.title,
              description: item.description || '',
              type: 'KT',
              credits: item.credits || 0,
              ...(item.nqf_level != null ? { nqf_level: item.nqf_level } : {}),
              subtasks: filterChildrenForContentGeneration(item.children || []).map(child => ({
                code: child.code,
                title: child.title,
                description: child.description || '',
                node_type: child.node_type,
                assessment_cues: child.assessment_cues || [],
                ...(child.nqf_level != null ? { nqf_level: child.nqf_level } : {})
              }))
            })),
            ...psItems.map(item => ({
              code: item.code,
              name: item.title,
              description: item.description || '',
              type: 'PS',
              credits: item.credits || 0,
              ...(item.nqf_level != null ? { nqf_level: item.nqf_level } : {}),
              subtasks: filterChildrenForContentGeneration(item.children || []).map(child => ({
                code: child.code,
                title: child.title,
                description: child.description || '',
                node_type: child.node_type,
                assessment_cues: child.assessment_cues || [],
                ...(child.nqf_level != null ? { nqf_level: child.nqf_level } : {})
              }))
            }))
          ]
        };
      }).filter(module => module.tasks.length > 0)
    };
  }, [modules, assignedItems, qualification]);

  const handleModalComplete = useCallback((completedPages) => {
    setPages(completedPages);
    setModalOpen(false);
  }, []);

  const startBuild = async () => {
    if (!contextTree || !contextTree.modules || contextTree.modules.length === 0) {
      setError('No knowledge tasks or practical skills found. Please assign tasks in previous steps.');
      return;
    }

    setBusy(true);
    setPages([]);
    setError(null);
    setDebugLogs([]);
    setStatus('generating');
    setModalOpen(true);

    // Generate run_id
    const currentRunId = `run-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setRunId(currentRunId);

    const totalCredits = (contextTree.modules || []).reduce((sum, mod) => {
      const taskCredits = (mod.tasks || []).reduce((tSum, t) => tSum + (Number(t.credits) || 0), 0);
      return sum + (taskCredits || Number(mod.credits) || 0);
    }, 0);
    const payload = {
      run_id: currentRunId,
      context_tree: contextTree,
      target_audience: qualification?.name ? `Learners in ${qualification.name}` : 'Learners',
      qualification_code: qualification?.code || qualification?.id?.toString(),
      qualification_id: qualification?.id,
      targetWordRange: wordRangeFromCredits(totalCredits),
      target_page_count: 30,
      audienceProfile: {
        role: 'Learner',
        ...(qualification?.nqf_level != null && qualification?.nqf_level !== ''
          ? { level: `NQF Level ${qualification.nqf_level}` }
          : {})
      },
      stylePrefs: { 
        readingLevel: "Upper-Intermediate", 
        region: "ZA" 
      },
      layoutPrefs: { 
        toc: true, 
        callouts: true 
      },
      citationStyle: "Harvard"
    };

    try {

      
      const res = await fetch('/api/learning/build-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const receivedPages = [];

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const chunk of parts) {
          if (!chunk.trim()) continue;
          
          const lines = chunk.split('\n').filter(Boolean);
          const evt = lines.find(l => l.startsWith('event: '));
          const dat = lines.find(l => l.startsWith('data: '));
          
          if (!evt || !dat) continue;

          const event = evt.replace('event: ', '').trim();
          let data;
          
          try {
            data = JSON.parse(dat.replace('data: ', ''));
          } catch (parseError) {

            continue;
          }



          switch (event) {
            case 'status':
              setStatus('generating');
              setDebugLogs(prev => [...prev, {
                level: 'info',
                message: data.message || 'Status update',
                timestamp: data.timestamp,
                details: data
              }]);
              break;

            case 'page':
              receivedPages.push(data);
              setPages([...receivedPages]);
              setDebugLogs(prev => [...prev, {
                level: 'info',
                message: `Page ${data.page_number || receivedPages.length} generated`,
                timestamp: new Date().toISOString(),
                details: {
                  page_number: data.page_number,
                  title: data.title,
                  word_count: data.approximate_word_count
                }
              }]);
              break;

            case 'debug':
              setDebugLogs(prev => [...prev, {
                level: data.level || 'info',
                message: data.message || 'Debug info',
                timestamp: data.timestamp || new Date().toISOString(),
                details: data
              }]);
              break;

            case 'done':
              setStatus('done');
              setBusy(false);
              if (data.allPages && Array.isArray(data.allPages)) {
                setPages(data.allPages);
              }
              setDebugLogs(prev => [...prev, {
                level: 'info',
                message: `Generation complete: ${data.pageCount || receivedPages.length} pages`,
                timestamp: data.timestamp || new Date().toISOString(),
                details: data
              }]);
              break;

            case 'error':
              setStatus('error');
              setBusy(false);
              setError(data.message || 'Generation failed');
              setDebugLogs(prev => [...prev, {
                level: 'error',
                message: data.message || 'Error occurred',
                timestamp: data.timestamp || new Date().toISOString(),
                details: data
              }]);
              break;

            default:

          }
        }
      }
    } catch (e) {

      setStatus('error');
      setBusy(false);
      setError(e.message || 'Build failed');
      setDebugLogs(prev => [...prev, {
        level: 'error',
        message: `Build error: ${e.message}`,
        timestamp: new Date().toISOString(),
        details: { error: e.message, stack: e.stack }
      }]);
    }
  };

  return (
    <>
      <Box sx={{ maxWidth: 900, margin: '0 auto', padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          Learning Content Builder (Agents)
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Generate PDF-ready learning content from Knowledge Tasks and Practical Skills with Harvard citations
        </Typography>
        
        {contextTree && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Found {contextTree.modules?.length || 0} module(s) with {contextTree.modules?.reduce((sum, m) => sum + (m.tasks?.length || 0), 0) || 0} task(s)
          </Alert>
        )}

        <Stack spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={startBuild}
              disabled={busy || !contextTree}
              startIcon={busy ? <CircularProgress size={20} /> : null}
            >
              {busy ? 'Building…' : 'Start Build'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.print()}
              disabled={pages.length === 0}
              startIcon={<PictureAsPdfIcon />}
            >
              Print / Save as PDF
            </Button>
          </Stack>

          {error && (
            <Alert severity="error">{error}</Alert>
          )}
        </Stack>

        <Stack spacing={2}>
          {pages.map((p, i) => (
            <Paper
              key={i}
              className="page"
              elevation={3}
              sx={{
                padding: 3,
                backgroundColor: '#fff',
                color: '#111'
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#667085',
                  display: 'block',
                  mb: 1
                }}
              >
                Page {p.page_number || i + 1}{p.title ? ' — ' + p.title : ''}
                {p.approximate_word_count && ` (${p.approximate_word_count} words)`}
              </Typography>
              {p.outline_path && p.outline_path.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Path: {p.outline_path.join(' → ')}
                </Typography>
              )}
              <LearnerReadingBody
                aria-label={p.title ? `Page ${p.page_number || i + 1}, ${p.title}` : `Page ${p.page_number || i + 1}`}
              >
                <div dangerouslySetInnerHTML={{ __html: p.html }} />
              </LearnerReadingBody>
            </Paper>
          ))}
        </Stack>

        {busy && pages.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Generating content...
            </Typography>
          </Box>
        )}
      </Box>

      <LearningProgressModal
        open={modalOpen}
        onClose={() => {
          if (!busy || error) {
            setModalOpen(false);
          }
        }}
        runId={runId}
        pages={pages}
        debugLogs={debugLogs}
        status={status}
        error={error}
        onComplete={handleModalComplete}
      />
    </>
  );
}
